import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  MessageSquare, 
  Heart, 
  Users, 
  Target, 
  Zap,
  Mic,
  Camera,
  Database,
  ArrowRight,
  ArrowLeft,
  CheckCircle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const goals = [
  { id: 'spouse', icon: Heart, label: 'Spouse', desc: 'Budget talks, financial goals', color: 'bg-pink-100 text-pink-600 border-pink-200' },
  { id: 'kids', icon: Users, label: 'Kids', desc: 'Allowance, saving, spending', color: 'bg-blue-100 text-blue-600 border-blue-200' },
  { id: 'parents', icon: Target, label: 'Parents', desc: 'Care costs, inheritance', color: 'bg-purple-100 text-purple-600 border-purple-200' },
  { id: 'retirement', icon: Zap, label: 'Retirement', desc: 'Planning, expectations', color: 'bg-amber-100 text-amber-600 border-amber-200' },
];

const skillFocuses = [
  { id: 'confidence', label: 'Confidence', desc: 'Speak clearly without hesitation' },
  { id: 'clarity', label: 'Clarity', desc: 'Get your point across effectively' },
  { id: 'tone', label: 'Tone', desc: 'Stay respectful under pressure' },
  { id: 'calm', label: 'Calm', desc: 'Keep cool in difficult moments' },
];

export default function Onboarding() {
  const [step, setStep] = useState(1);
  const [primaryGoal, setPrimaryGoal] = useState('');
  const [comfortLevel, setComfortLevel] = useState([50]);
  const [skillFocus, setSkillFocus] = useState('');
  const [consents, setConsents] = useState({
    camera: false,
    mic: false,
    dataRetention: false,
  });
  const [loading, setLoading] = useState(false);

  const { token, updateUser } = useAuth();
  const navigate = useNavigate();

  const comfortLabels = ['Low', 'Medium', 'High'];
  const getComfortLabel = (value) => {
    if (value < 33) return 'low';
    if (value < 66) return 'medium';
    return 'high';
  };

  const handleComplete = async () => {
    if (!primaryGoal || !skillFocus) {
      toast.error('Please complete all required fields');
      return;
    }

    if (!consents.camera || !consents.mic) {
      toast.error('Camera and microphone permissions are required for practice sessions');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.put(`${API}/users/onboarding`, {
        primary_goal: primaryGoal,
        comfort_level: getComfortLabel(comfortLevel[0]),
        skill_focus: skillFocus,
        consent_camera: consents.camera,
        consent_mic: consents.mic,
        consent_data_retention: consents.dataRetention,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      updateUser(response.data);
      toast.success('Profile setup complete!');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Failed to save preferences. Please try again.');
    }
    setLoading(false);
  };

  const canProceed = () => {
    switch (step) {
      case 1: return !!primaryGoal;
      case 2: return true;
      case 3: return !!skillFocus;
      case 4: return consents.camera && consents.mic;
      default: return false;
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <header className="border-b border-gray-100 px-4 py-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#00E085] rounded-lg flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-lg tracking-tight">
              <span className="text-[#111827]">LET&apos;S</span>
              <span className="text-[#00E085]">TALK</span>
              <span className="text-[#111827]">MONEY</span>
            </span>
          </div>
          <span className="text-sm text-gray-500">Step {step} of 4</span>
        </div>
      </header>

      {/* Progress bar */}
      <div className="w-full bg-gray-100">
        <div 
          className="h-1 bg-[#00E085] transition-all duration-500"
          style={{ width: `${(step / 4) * 100}%` }}
        />
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col items-center justify-center px-4 py-12">
        <div className="max-w-xl w-full">
          {/* Step 1: Primary Goal */}
          {step === 1 && (
            <div className="animate-fade-in" data-testid="onboarding-step-1">
              <h1 className="text-3xl font-bold text-[#111827] mb-4 text-center">
                Who do you want to talk to about money?
              </h1>
              <p className="text-gray-600 text-center mb-10">
                Choose your primary conversation partner. You can practice others later.
              </p>
              
              <div className="grid sm:grid-cols-2 gap-4">
                {goals.map((goal) => (
                  <button
                    key={goal.id}
                    onClick={() => setPrimaryGoal(goal.id)}
                    className={`p-6 rounded-2xl border-2 text-left transition-all ${
                      primaryGoal === goal.id
                        ? 'border-[#00E085] bg-[#00E085]/5'
                        : 'border-gray-100 hover:border-gray-200'
                    }`}
                    data-testid={`goal-${goal.id}`}
                  >
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${goal.color}`}>
                      <goal.icon className="w-6 h-6" />
                    </div>
                    <h3 className="font-semibold text-[#111827] mb-1">{goal.label}</h3>
                    <p className="text-sm text-gray-500">{goal.desc}</p>
                    {primaryGoal === goal.id && (
                      <CheckCircle className="w-5 h-5 text-[#00E085] absolute top-4 right-4" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Comfort Level */}
          {step === 2 && (
            <div className="animate-fade-in" data-testid="onboarding-step-2">
              <h1 className="text-3xl font-bold text-[#111827] mb-4 text-center">
                How comfortable are you discussing money?
              </h1>
              <p className="text-gray-600 text-center mb-10">
                This helps us adjust the difficulty and coaching style.
              </p>
              
              <div className="bg-[#F8F9FA] rounded-2xl p-8">
                <div className="flex justify-between mb-4 text-sm font-medium text-gray-600">
                  <span>Low comfort</span>
                  <span>High comfort</span>
                </div>
                
                <Slider
                  value={comfortLevel}
                  onValueChange={setComfortLevel}
                  max={100}
                  step={1}
                  className="mb-6"
                  data-testid="comfort-slider"
                />
                
                <div className="text-center">
                  <span className={`inline-block px-4 py-2 rounded-full text-sm font-semibold ${
                    comfortLevel[0] < 33 
                      ? 'bg-red-100 text-red-700' 
                      : comfortLevel[0] < 66 
                        ? 'bg-yellow-100 text-yellow-700'
                        : 'bg-green-100 text-green-700'
                  }`}>
                    {getComfortLabel(comfortLevel[0]).charAt(0).toUpperCase() + getComfortLabel(comfortLevel[0]).slice(1)} Comfort
                  </span>
                </div>
              </div>
              
              <p className="text-sm text-gray-500 text-center mt-6">
                {comfortLevel[0] < 33 && "We'll start with gentle scenarios and more coaching support."}
                {comfortLevel[0] >= 33 && comfortLevel[0] < 66 && "We'll balance challenge with support."}
                {comfortLevel[0] >= 66 && "We'll offer more challenging scenarios and direct feedback."}
              </p>
            </div>
          )}

          {/* Step 3: Skill Focus */}
          {step === 3 && (
            <div className="animate-fade-in" data-testid="onboarding-step-3">
              <h1 className="text-3xl font-bold text-[#111827] mb-4 text-center">
                What skill do you want to improve most?
              </h1>
              <p className="text-gray-600 text-center mb-10">
                We&apos;ll tailor feedback to help you grow in this area.
              </p>
              
              <div className="space-y-4">
                {skillFocuses.map((skill) => (
                  <button
                    key={skill.id}
                    onClick={() => setSkillFocus(skill.id)}
                    className={`w-full p-5 rounded-2xl border-2 text-left transition-all flex items-center justify-between ${
                      skillFocus === skill.id
                        ? 'border-[#00E085] bg-[#00E085]/5'
                        : 'border-gray-100 hover:border-gray-200'
                    }`}
                    data-testid={`skill-${skill.id}`}
                  >
                    <div>
                      <h3 className="font-semibold text-[#111827] mb-1">{skill.label}</h3>
                      <p className="text-sm text-gray-500">{skill.desc}</p>
                    </div>
                    {skillFocus === skill.id && (
                      <CheckCircle className="w-6 h-6 text-[#00E085]" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 4: Consent */}
          {step === 4 && (
            <div className="animate-fade-in" data-testid="onboarding-step-4">
              <h1 className="text-3xl font-bold text-[#111827] mb-4 text-center">
                Almost there! A few permissions...
              </h1>
              <p className="text-gray-600 text-center mb-10">
                These are needed for the practice sessions to work properly.
              </p>
              
              <div className="space-y-4">
                <div className={`p-5 rounded-2xl border-2 transition-all ${
                  consents.camera ? 'border-[#00E085] bg-[#00E085]/5' : 'border-gray-100'
                }`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center">
                        <Camera className="w-5 h-5 text-gray-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-[#111827]">Camera Access</h3>
                        <p className="text-sm text-gray-500">Required for video practice</p>
                      </div>
                    </div>
                    <Switch
                      checked={consents.camera}
                      onCheckedChange={(checked) => setConsents({ ...consents, camera: checked })}
                      data-testid="consent-camera"
                    />
                  </div>
                </div>

                <div className={`p-5 rounded-2xl border-2 transition-all ${
                  consents.mic ? 'border-[#00E085] bg-[#00E085]/5' : 'border-gray-100'
                }`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center">
                        <Mic className="w-5 h-5 text-gray-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-[#111827]">Microphone Access</h3>
                        <p className="text-sm text-gray-500" data-testid="microphone-permission-description">Required for live voice conversations</p>
                      </div>
                    </div>
                    <Switch
                      checked={consents.mic}
                      onCheckedChange={(checked) => setConsents({ ...consents, mic: checked })}
                      data-testid="consent-mic"
                    />
                  </div>
                </div>

                <div className={`p-5 rounded-2xl border-2 transition-all ${
                  consents.dataRetention ? 'border-[#00E085] bg-[#00E085]/5' : 'border-gray-100'
                }`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center">
                        <Database className="w-5 h-5 text-gray-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-[#111827]">Data Retention</h3>
                        <p className="text-sm text-gray-500">Save transcripts for progress tracking (optional)</p>
                      </div>
                    </div>
                    <Switch
                      checked={consents.dataRetention}
                      onCheckedChange={(checked) => setConsents({ ...consents, dataRetention: checked })}
                      data-testid="consent-data"
                    />
                  </div>
                </div>
              </div>

              <p className="text-xs text-gray-400 text-center mt-6">
                We never store raw video. Only text transcripts and scores are saved.
                You can delete your data at any time.
              </p>
            </div>
          )}

          {/* Navigation buttons */}
          <div className="flex items-center justify-between mt-10">
            <Button
              variant="ghost"
              onClick={() => setStep(step - 1)}
              disabled={step === 1}
              className="text-gray-600"
              data-testid="onboarding-back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>

            {step < 4 ? (
              <Button
                onClick={() => setStep(step + 1)}
                disabled={!canProceed()}
                className="bg-[#111827] text-white hover:bg-[#1F2937] rounded-full px-8"
                data-testid="onboarding-next-btn"
              >
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={handleComplete}
                disabled={!canProceed() || loading}
                className="bg-[#00E085] text-[#00331E] hover:bg-[#00C978] rounded-full px-8"
                data-testid="onboarding-complete-btn"
              >
                {loading ? 'Saving...' : 'Start Practicing'}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
